import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { FileText, Loader2 } from 'lucide-react';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/seo/admin/regenerate-intros`;

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl bg-gray-50 px-3 py-2" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
    <p className="text-lg font-bold text-[#064E3B] tabular-nums">{value ?? 0}</p>
  </div>
);

// Regenerates every SEO intro text from current data; old texts are backed up.
export default function SeoIntroRegenCard() {
  const [status, setStatus] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const timer = useRef(null);
  const wasRunning = useRef(false);

  const poll = async () => {
    try {
      const { data } = await axios.get(`${BASE}/status`);
      setStatus(data);
      if (wasRunning.current && !data.running) {
        toast.success(`Textos regenerados: ${data.replaced + data.created} · fallidos: ${data.failed}`);
      }
      wasRunning.current = data.running;
      clearTimeout(timer.current);
      if (data.running) timer.current = setTimeout(poll, 2500);
    } catch (e) {
      toast.error('No se pudo leer el avance');
    }
  };

  useEffect(() => { poll(); return () => clearTimeout(timer.current); }, []);

  const start = async () => {
    setConfirmOpen(false);
    try {
      await axios.post(BASE);
      wasRunning.current = true;
      poll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar');
    }
  };

  const running = !!status?.running;
  const pct = status?.total ? Math.round((status.processed / status.total) * 100) : 0;

  return (
    <Card className="rounded-2xl border-0 shadow-sm mb-6" data-testid="seo-regen-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="w-5 h-5 text-[#00CC66]" /> Regenerar textos SEO de comunas y categorías
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">
          Reescribe con IA la introducción de cada página de comuna y categoría usando los datos actuales
          (recintos, categorías y eventos realmente gratuitos). Los textos anteriores quedan respaldados.
        </p>
        <Button onClick={() => setConfirmOpen(true)} disabled={running} className="btn-primary rounded-full" data-testid="seo-regen-start-btn">
          {running ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {running ? 'Regenerando…' : 'Regenerar textos'}
        </Button>
        {status?.started_at && (
          <div className="space-y-3" data-testid="seo-regen-progress">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{running ? 'En curso' : `Última ejecución: ${new Date(status.finished_at || status.started_at).toLocaleString('es-CL')}`}</span>
              <span className="font-semibold tabular-nums" data-testid="seo-regen-count">{status.processed}/{status.total}</span>
            </div>
            <Progress value={pct} className="h-2" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Reemplazados" value={status.replaced} testId="seo-regen-replaced" />
              <Stat label="Nuevos" value={status.created} testId="seo-regen-created" />
              <Stat label="Retirados (<3 eventos)" value={status.removed} testId="seo-regen-removed" />
              <Stat label="Fallidos" value={status.failed} testId="seo-regen-failed" />
            </div>
            {status.last_error && <p className="text-xs text-red-500" data-testid="seo-regen-error">{status.last_error}</p>}
          </div>
        )}
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="seo-regen-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Regenerar todos los textos SEO?</AlertDialogTitle>
            <AlertDialogDescription>
              Se reescriben unas 140 páginas con los datos actuales. Los textos vigentes se respaldan antes de reemplazarlos. Tarda unos minutos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="seo-regen-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={start} data-testid="seo-regen-confirm-btn">Sí, regenerar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
